/* =============================================================
   Cookie consent bar — scripts/CookieConsent.jsx
   Imported by the component itself rather than linked per page, because
   /onboarding loads onboarding-styles.css instead of styles.css. For the
   same reason the bar carries its own button styles: it only depends on
   the tokens in colors_and_type.css, which every page links.

   Bottom anchored and deliberately non blocking: the layer is
   pointer-events:none so only the card is interactive, with no scrim and
   no scroll lock. Sits above the nav (z 60) and below the lead modal
   (z 100), so an open modal is never covered.
   ============================================================= */
.sw-cookie-layer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  display: flex;
  justify-content: center;
  padding: 16px;
  padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
  pointer-events: none;
}

.sw-cookie {
  pointer-events: auto;
  width: 100%;
  max-width: var(--container-max, 1280px);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 18px 20px;
  font-family: var(--font-sans);
  background: var(--surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  animation: sw-cookie-in 260ms var(--ease-out);
}

@keyframes sw-cookie-in {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

.sw-cookie.is-prefs {
  max-width: 760px;
  flex-direction: column;
  align-items: stretch;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}

/* The basis is a width only while the card is a row. Every stacked layout below
   resets it, otherwise flex-basis becomes a 360px minimum height and the card
   opens with a tall empty gap under the text. */
.sw-cookie-copy { flex: 1 1 360px; min-width: 0; }
.sw-cookie.is-prefs .sw-cookie-copy { flex: 0 0 auto; }

.sw-cookie-title {
  /* Focused programmatically when the panel is reopened, so it needs a visible
     ring — fit-content keeps that ring on the words instead of the whole card. */
  width: fit-content;
  max-width: 100%;
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.4;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.sw-cookie-title:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 4px; }
.sw-cookie-title-short { display: none; }

.sw-cookie-text {
  font-size: 13px;
  line-height: 1.55;
  color: var(--text-secondary);
  margin: 0;
}
.sw-cookie-text a {
  color: var(--accent-blue);
  font-weight: 500;
  text-decoration: none;
}

/* ---------- Per category rows (preferences view) ---------- */
.sw-cookie-cats { display: grid; gap: 8px; }

.sw-cookie-cat {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  background: var(--bg-sunken);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}

.sw-cookie-cat-main { min-width: 0; }

.sw-cookie-cat-name {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
  cursor: pointer;
}
.sw-cookie-cat.is-locked .sw-cookie-cat-name { cursor: default; }

.sw-cookie-cat-desc {
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--text-secondary);
  margin: 0;
}

.sw-cookie-cat-input {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: var(--accent-blue);
  cursor: pointer;
}

.sw-cookie-cat-always {
  flex: none;
  margin-top: 2px;
  font-size: var(--fs-xs);
  font-weight: 600;
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--text-tertiary);
  white-space: nowrap;
}

/* ---------- Actions ---------- */
.sw-cookie-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: none;
}
.sw-cookie.is-prefs .sw-cookie-actions { justify-content: flex-end; }

.sw-cookie-btn {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  padding: 9px 14px;
  border-radius: 10px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: background-color var(--dur-micro) var(--ease-out),
              border-color var(--dur-micro) var(--ease-out),
              color var(--dur-micro) var(--ease-out);
}

.sw-cookie-btn--quiet {
  background: var(--surface);
  color: var(--text-primary);
  border: 1px solid var(--border-strong);
}
.sw-cookie-btn--quiet:hover { background: var(--surface-hover); }

.sw-cookie-btn--primary {
  background: var(--accent-blue);
  color: #fff;
  border: 1px solid var(--accent-blue);
}
.sw-cookie-btn--primary:hover { background: var(--accent-blue-600); border-color: var(--accent-blue-600); }
.sw-cookie-btn--primary:active { background: var(--accent-blue-700); border-color: var(--accent-blue-700); }

@media (max-width: 860px) {
  .sw-cookie { flex-direction: column; align-items: stretch; gap: 14px; }
  .sw-cookie-copy { flex: 0 0 auto; }
  .sw-cookie-actions { justify-content: stretch; }
  .sw-cookie-btn { flex: 1 1 auto; }
}

@media (max-width: 520px) {
  .sw-cookie-layer { padding: 10px; padding-bottom: max(10px, env(safe-area-inset-bottom, 0px)); }
  .sw-cookie { padding: 16px; }
  /* Stacked in DOM order, so the primary action lands at the bottom of a bottom
     anchored sheet (nearest the thumb) and tab order still matches what is seen. */
  .sw-cookie-actions { flex-direction: column; align-items: stretch; }
}

/* Compact first-visit ask on phones: the full paragraph plus stacked buttons
   covered the hero form and trust copy on a 390px screen. The bar collapses to
   title + privacy link + one row of buttons; the full explanation and
   per-category copy stay one tap away behind "Manage cookies" (the preferences
   view keeps its complete layout, which the :not(.is-prefs) guard leaves
   untouched). The hidden body copy is still announced: aria-describedby
   includes display:none targets in the accessible description. */
@media (max-width: 640px) {
  .sw-cookie:not(.is-prefs) { padding: 12px 14px; gap: 8px 10px; }
  /* Title and privacy link share one row: short "Cookies" on the left, the
     policy link on the right. The full title span swaps out with it. */
  .sw-cookie:not(.is-prefs) .sw-cookie-copy { display: flex; align-items: baseline; justify-content: space-between; gap: 6px 12px; }
  .sw-cookie:not(.is-prefs) .sw-cookie-title { font-size: var(--fs-sm); margin: 0; }
  /* Visually hidden, NOT display:none: the full title is what the dialog's
     aria-labelledby resolves to. display:none emptied the <h2> for assistive
     tech on phones, which left the dialog with no accessible name at all. */
  .sw-cookie:not(.is-prefs) .sw-cookie-title-full {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; border: 0;
  }
  .sw-cookie:not(.is-prefs) .sw-cookie-title-short { display: inline; }
  .sw-cookie:not(.is-prefs) .sw-cookie-body { display: none; }
  .sw-cookie:not(.is-prefs) .sw-cookie-text { flex: none; font-size: 12px; }
  .sw-cookie:not(.is-prefs) .sw-cookie-actions { flex-direction: row; flex-wrap: wrap; }
  .sw-cookie:not(.is-prefs) .sw-cookie-btn { flex: 1 1 auto; padding: 8px 10px; font-size: 12.5px; }
}

@media (prefers-reduced-motion: reduce) {
  .sw-cookie { animation: none; }
}
